<template>
  <div class="page-container">
    <Sidebar />
    <div class="main-content">
      <div class="page-header">
        <h1>问询管理</h1>
      </div>

      <div class="filter-bar">
        <input v-model="searchQuery" type="text" class="search-input" placeholder="搜索药材名或问询内容..." @keyup.enter="handleSearch" />
        <select v-model="statusFilter" class="status-select" @change="handleSearch">
          <option value="">全部状态</option>
          <option value="pending">待回复</option>
          <option value="replied">已回复</option>
        </select>
      </div>

      <div class="content-body">
        <div class="loading" v-if="loading">加载中...</div>
        <div class="error" v-else-if="error">{{ error }}</div>
        <table class="inquiries-table" v-else>
          <thead>
            <tr>
              <th>ID</th>
              <th>用户手机</th>
              <th>药材名</th>
              <th>问询内容</th>
              <th>回复</th>
              <th>状态</th>
              <th>创建时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in inquiries" :key="item.id">
              <td>{{ item.id }}</td>
              <td>{{ item.user_phone || '-' }}</td>
              <td>{{ item.herb_name || '-' }}</td>
              <td class="question-cell">{{ item.question || '-' }}</td>
              <td class="answer-cell">{{ item.answer || '-' }}</td>
              <td>
                <StatusTag :status="item.status" :custom-map="{ pending: '待回复', replied: '已回复' }" />
              </td>
              <td>{{ formatDate(item.created_at) }}</td>
              <td class="actions">
                <button class="reply-btn" @click="openReplyModal(item)" :disabled="item.status === 'replied'">回复</button>
                <button class="view-btn" @click="viewDetail(item)">详情</button>
                <button class="delete-btn" @click="deleteInquiry(item.id)">删除</button>
              </td>
            </tr>
            <tr v-if="inquiries.length === 0">
              <td colspan="8" class="empty">暂无问询记录</td>
            </tr>
          </tbody>
        </table>

        <div class="pagination" v-if="totalPages > 1">
          <button :disabled="currentPage === 1" @click="changePage(currentPage - 1)">上一页</button>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">下一页</button>
        </div>

        <div class="total" v-if="!loading && !error">共 {{ total }} 条记录</div>
      </div>
    </div>

    <div class="modal" v-if="showReplyModal">
      <div class="modal-content">
        <h2>回复问询</h2>
        <div class="detail-section">
          <div class="detail-item">
            <label>用户手机:</label>
            <span>{{ currentInquiry.user_phone || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>药材名:</label>
            <span>{{ currentInquiry.herb_name || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>问询内容:</label>
            <p class="question-text">{{ currentInquiry.question || '-' }}</p>
          </div>
        </div>
        <div class="form-group">
          <label>回复内容:</label>
          <textarea v-model="replyContent" placeholder="请输入回复内容..." rows="5"></textarea>
        </div>
        <div class="form-actions">
          <button type="button" class="cancel-btn" @click="closeReplyModal">取消</button>
          <button type="button" class="submit-btn" @click="submitReply">提交回复</button>
        </div>
      </div>
    </div>

    <div class="modal" v-if="showDetailModal">
      <div class="modal-content">
        <h2>问询详情</h2>
        <div class="detail-section">
          <div class="detail-item">
            <label>ID:</label>
            <span>{{ currentInquiry.id }}</span>
          </div>
          <div class="detail-item">
            <label>用户手机:</label>
            <span>{{ currentInquiry.user_phone || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>药材名:</label>
            <span>{{ currentInquiry.herb_name || '-' }}</span>
          </div>
          <div class="detail-item">
            <label>问询内容:</label>
            <p class="question-text">{{ currentInquiry.question || '-' }}</p>
          </div>
          <div class="detail-item">
            <label>回复内容:</label>
            <p class="answer-text">{{ currentInquiry.answer || '暂无回复' }}</p>
          </div>
          <div class="detail-item">
            <label>状态:</label>
            <span :class="['status', currentInquiry.status === 'replied' ? 'replied' : 'pending']">
              {{ currentInquiry.status === 'replied' ? '已回复' : '待回复' }}
            </span>
          </div>
          <div class="detail-item">
            <label>创建时间:</label>
            <span>{{ formatDate(currentInquiry.created_at) }}</span>
          </div>
          <div class="detail-item" v-if="currentInquiry.updated_at">
            <label>更新时间:</label>
            <span>{{ formatDate(currentInquiry.updated_at) }}</span>
          </div>
        </div>
        <div class="form-actions">
          <button type="button" class="cancel-btn" @click="closeDetailModal">关闭</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import StatusTag from '../components/StatusTag.vue'
import axios from 'axios'
import { formatDate } from '../utils/dateFormat'
import { getAdminPassword } from '../utils/api'

export default {
  name: 'InquiriesList',
  components: { Sidebar, StatusTag },
  data() {
    return {
      inquiries: [],
      loading: true,
      error: '',
      searchQuery: '',
      statusFilter: '',
      currentPage: 1,
      pageSize: 20,
      total: 0,
      showReplyModal: false,
      showDetailModal: false,
      currentInquiry: {},
      replyContent: ''
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    }
  },
  mounted() {
    this.fetchInquiries()
  },
  methods: {
    async fetchInquiries() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          search: this.searchQuery || undefined,
          status: this.statusFilter || undefined
        }
        const response = await axios.get('/api/admin/inquiries', {
          params,
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          const data = response.data.data
          this.inquiries = data.list || []
          this.total = data.total || 0
        } else {
          this.error = response.data.msg
        }
      } catch (err) {
        this.error = '网络错误'
      } finally {
        this.loading = false
      }
    },
    formatDate(dateStr) {
      return formatDate(dateStr)
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchInquiries()
    },
    changePage(page) {
      this.currentPage = page
      this.fetchInquiries()
    },
    openReplyModal(item) {
      this.currentInquiry = item
      this.replyContent = item.answer || ''
      this.showReplyModal = true
    },
    closeReplyModal() {
      this.showReplyModal = false
      this.currentInquiry = {}
      this.replyContent = ''
    },
    viewDetail(item) {
      this.currentInquiry = item
      this.showDetailModal = true
    },
    closeDetailModal() {
      this.showDetailModal = false
      this.currentInquiry = {}
    },
    async submitReply() {
      if (!this.replyContent.trim()) {
        alert('请输入回复内容')
        return
      }
      try {
        const response = await axios.put(`/api/admin/inquiries/${this.currentInquiry.id}/reply`, 
          { answer: this.replyContent },
          { headers: { 'x-admin-password': getAdminPassword() } }
        )
        if (response.data.code === 200) {
          alert('回复成功')
          this.closeReplyModal()
          this.fetchInquiries()
        } else {
          alert(response.data.msg || '回复失败')
        }
      } catch (err) {
        alert('网络错误')
      }
    },
    async deleteInquiry(id) {
      if (!confirm('确定删除此问询记录？')) return
      try {
        const response = await axios.delete(`/api/admin/inquiries/${id}`, {
          headers: { 'x-admin-password': getAdminPassword() }
        })
        if (response.data.code === 200) {
          this.fetchInquiries()
        } else {
          alert(response.data.msg || '删除失败')
        }
      } catch (err) {
        alert('网络错误')
      }
    }
  }
}
</script>

<style scoped>
.page-container { display: flex; min-height: 100vh; background: #f5f5f5; }
.main-content { flex: 1; margin-left: 260px; padding: 30px; }
.page-header { margin-bottom: 20px; }
.page-header h1 { font-size: 24px; color: #2c3e50; }
.filter-bar { display: flex; gap: 10px; margin-bottom: 20px; }
.search-input { padding: 10px 15px; border: 1px solid #ddd; border-radius: 5px; flex: 1; min-width: 250px; }
.status-select { padding: 10px 15px; border: 1px solid #ddd; border-radius: 5px; background: white; }
.content-body { background: white; padding: 20px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
.loading, .error, .empty { text-align: center; padding: 50px; color: #666; }
.error { color: #e74c3c; }
.inquiries-table { width: 100%; border-collapse: collapse; }
.inquiries-table th { background: #f8f9fa; padding: 15px; text-align: left; font-weight: 600; color: #333; border-bottom: 2px solid #eee; }
.inquiries-table td { padding: 15px; border-bottom: 1px solid #eee; color: #666; }
.inquiries-table tr:hover { background: #f8f9fa; }
.question-cell, .answer-cell { max-width: 200px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.status { padding: 4px 12px; border-radius: 4px; font-size: 12px; }
.status.pending { background: #fff3cd; color: #856404; }
.status.replied { background: #e8f5e9; color: #4caf50; }
.actions { display: flex; gap: 10px; }
.reply-btn, .view-btn, .delete-btn { padding: 6px 12px; border: none; border-radius: 5px; cursor: pointer; font-size: 12px; }
.reply-btn { background: #3498db; color: white; }
.reply-btn:disabled { background: #ccc; cursor: not-allowed; }
.view-btn { background: #9b59b6; color: white; }
.delete-btn { background: #e74c3c; color: white; }
.pagination { display: flex; justify-content: center; align-items: center; gap: 15px; margin-top: 20px; }
.pagination button { padding: 8px 15px; background: white; border: 1px solid #ddd; border-radius: 5px; cursor: pointer; }
.pagination button:disabled { opacity: 0.5; cursor: not-allowed; }
.total { margin-top: 15px; text-align: center; color: #666; }
.modal { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.modal-content { background: white; padding: 30px; border-radius: 10px; width: 500px; max-height: 90vh; overflow-y: auto; }
.modal-content h2 { margin: 0 0 20px; color: #2c3e50; }
.detail-section { margin-bottom: 20px; }
.detail-item { margin-bottom: 15px; }
.detail-item label { display: block; margin-bottom: 5px; color: #666; font-weight: 500; }
.detail-item span { color: #333; }
.question-text, .answer-text { padding: 10px; background: #f5f5f5; border-radius: 5px; margin: 0; word-break: break-word; }
.form-group { margin-bottom: 15px; }
.form-group label { display: block; margin-bottom: 5px; color: #666; }
.form-group textarea { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; resize: vertical; min-height: 100px; }
.form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.cancel-btn, .submit-btn { padding: 10px 20px; border: none; border-radius: 5px; cursor: pointer; }
.cancel-btn { background: #ddd; color: #333; }
.submit-btn { background: #3498db; color: white; }
</style>